import React from "react"
import cx from "classnames"

import Badge from "@kit/design/badge"
import Button from "@kit/design/button"
import useScreenDimensions from "hooks/use-screen-dimensions"

import FeatureList from "./plan-feature-list"

import { usePlanComparisonContext } from "./context"
import analytics from "utils/analytics"

const PlanCard = ({ plan, buttonText, multiplePlans = true }) => {
  const {
    handlePlanSelection,
    prioritizedServiceLevel
  } = usePlanComparisonContext()

  const featured =
    plan.prioritize_plan ||
    prioritizedServiceLevel === plan.service_level ||
    plan.service_level === "creator"

  const { width } = useScreenDimensions()

  const isLargeScreen = width >= 1024
  const highlight = featured && multiplePlans && isLargeScreen

  const price = plan.interval === "year" ? plan.monthly_amount : plan.text
  const savings = plan.annual_savings
  const defaultButtonText = `Upgrade to ${plan.display_name}`

  function handleClick(plan) {
    analytics.track("Element", "Clicked", {
      element_type: "button",
      name: "plan_comparison_upgrade_button",
      text: buttonText || defaultButtonText,
      service_level: plan.service_level
    })

    handlePlanSelection(plan)
  }

  return (
    <div
      data-testid={`${plan.service_level}-features`}
      className={cx(
        "bg-white lg:p-6 rounded-lg lg:border flex flex-col h-full",
        {
          ["border-green-400"]: highlight
        }
      )}
    >
      <div className="flex flex-col flex-grow">
        <div className="flex flex-row justify-between items-center h-auto mb-1">
          <span
            className="font-semibold text-xl text-black font-heading"
            data-testid="plan-header"
          >
            {plan.display_name}
          </span>
          {featured && multiplePlans && (
            <Badge color="green" variant="light" size="lg">
              Recommended
            </Badge>
          )}
        </div>

        <p className="mb-3">{plan.description}</p>

        <div className="mb-5">
          <div className="text-black" data-testid="price-display">
            <span className="text-4xl font-heading">{price}</span>
            <span className="text-xl" aria-label="per month">
              /mo
            </span>
          </div>

          <div className="text-gray-700 -mt-1">
            <span className="font-bold">{plan.text}</span> billed{" "}
            {plan.interval}
            ly
            {plan.interval == "year" && (
              <span className="italic"> (Save {savings} per year)</span>
            )}
          </div>
        </div>

        <FeatureList plan={plan} />
      </div>

      <Button
        data-testid={`upgrade-button-${plan.id}`}
        className="w-full"
        size="lg"
        is={featured ? "primary" : "secondary"}
        onClick={() => handleClick(plan)}
      >
        {buttonText || defaultButtonText}
      </Button>
    </div>
  )
}

export default PlanCard
